import axios from 'axios';
import { API_BASE } from './config';

/**
 * Create a withdrawal transaction using the alternative API
 * This API doesn't require a private key as it can retrieve it from the database
 *
 * @param data Withdrawal data
 * @returns Withdrawal transaction result
 */
export const createWithdrawal = async (data: {
  from_address?: string;
  to: string;
  amount: string;
  coin: string;
  network: string;
  user_id: number;
  privateKey?: string;
  tag?: string;
  contract_address?: string;
  priority?: 'slow' | 'medium' | 'fast';
}) => {
  try {
    // Ensure user_id is a number
    const userId = typeof data.user_id === 'string' ? parseInt(data.user_id, 10) : data.user_id;

    // Create a new object with the correct parameter types
    const requestData = {
      ...data,
      user_id: userId, // Ensure user_id is a number
      privateKey: data.privateKey
    };

    console.log('Creating withdrawal with data:', {
      ...requestData,
      privateKey: requestData.privateKey ? '[REDACTED]' : undefined,
      amount: requestData.amount,
      to: requestData.to,
      from_address: requestData.from_address,
      user_id: requestData.user_id,
      user_id_type: typeof requestData.user_id
    });

    const response = await axios.post(`${API_BASE}/withdrawal/withdraw`, requestData);

    console.log('Withdrawal API response:', response.data);

    // Return the response data as is
    return response.data;
  } catch (error) {
    console.error('Error in createWithdrawal API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);

      // Check if it's a validation error
      const responseData = error.response.data;
      if (responseData.error && typeof responseData.error === 'string' && responseData.error.includes('validation failed')) {
        console.error('Validation error details:', responseData.error);

        // Return a more user-friendly error message
        return {
          status: false,
          error: responseData.error,
          errorType: 'VALIDATION_ERROR',
          friendlyMessage: 'Please check your input. Some required fields are missing or invalid.'
        };
      }

      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      status: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Reset a stuck withdrawal transaction by ID
 *
 * @param id Transaction ID
 * @param adminNotes Optional admin notes
 * @returns Reset result
 */
export const resetWithdrawalTransaction = async (id: string, adminNotes?: string) => {
  try {
    console.log(`Resetting withdrawal transaction: ${id}`);

    const response = await axios.post(`${API_BASE}/wallet/admin/reset-transaction`, {
      transactionId: id,
      adminNotes: adminNotes || 'Reset by admin'
    });

    console.log('Reset transaction API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in resetWithdrawalTransaction API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      status: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Reset stuck withdrawal transactions
 *
 * @param options Reset options
 * @returns Reset result
 */
export const resetStuckWithdrawalTransactions = async (options: {
  olderThanMinutes?: number;
  statuses?: string[];
} = {}) => {
  try {
    const { olderThanMinutes = 10, statuses = ['Broadcast', 'Confirmed'] } = options;

    console.log(`Resetting stuck withdrawal transactions older than ${olderThanMinutes} minutes with statuses: ${statuses.join(', ')}`);

    const response = await axios.post(`${API_BASE}/withdrawal/reset-stuck`, {
      olderThanMinutes,
      statuses
    });

    console.log('Reset stuck transactions API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in resetStuckWithdrawalTransactions API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      status: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Get stuck withdrawal transactions
 *
 * @param options Options for fetching stuck transactions
 * @returns List of stuck transactions
 */
export const getStuckWithdrawalTransactions = async (options: {
  olderThanMinutes?: number;
  limit?: number;
  status?: string;
  requiresManualIntervention?: boolean;
} = {}) => {
  try {
    const {
      olderThanMinutes = 30,
      limit = 50,
      status = 'Broadcast',
      requiresManualIntervention = false
    } = options;

    console.log(`Fetching stuck withdrawal transactions older than ${olderThanMinutes} minutes with status: ${status}`);
    if (requiresManualIntervention) {
      console.log('Filtering for transactions that require manual intervention');
    }

    const response = await axios.get(`${API_BASE}/wallet/admin/stuck-transactions`, {
      params: {
        olderThanMinutes,
        limit,
        status,
        requiresManualIntervention
      }
    });

    console.log('Get stuck transactions API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in getStuckWithdrawalTransactions API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Manually confirm a withdrawal transaction
 *
 * @param options Options for manually confirming a transaction
 * @returns Result of the manual confirmation
 */
export const manuallyConfirmTransaction = async (options: {
  transactionId: string;
  adminNotes?: string;
}) => {
  try {
    const { transactionId, adminNotes = 'Manually confirmed by admin' } = options;

    console.log(`Manually confirming transaction: ${transactionId}`);

    const response = await axios.post(`${API_BASE}/wallet/admin/manually-confirm-transaction`, {
      transactionId,
      adminNotes
    });

    console.log('Manual confirmation API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in manuallyConfirmTransaction API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Re-queue a stuck withdrawal transaction
 *
 * @param transactionId Transaction ID to re-queue
 * @param adminNotes Optional admin notes
 * @returns Result of the re-queue operation
 */
export const requeueWithdrawalTransaction = async (
  transactionId: string,
  adminNotes?: string
) => {
  try {
    console.log(`Re-queuing withdrawal transaction ${transactionId}`);

    const response = await axios.post(`${API_BASE}/wallet/admin/reset-transaction`, {
      transactionId,
      adminNotes: adminNotes || 'Re-queued by admin from dashboard'
    });

    console.log('Re-queue response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error re-queuing withdrawal transaction:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Force a state change on a withdrawal transaction
 *
 * @param options Options for forcing a state change
 * @returns Result of the state change
 */
export const forceStateChange = async (options: {
  transactionId: string;
  targetState: string;
  adminNotes?: string;
}) => {
  try {
    const { transactionId, targetState, adminNotes = 'State change forced by admin' } = options;

    console.log(`Forcing state change for transaction ${transactionId} to ${targetState}`);

    const response = await axios.post(`${API_BASE}/wallet/admin/force-state-change`, {
      transactionId,
      targetState,
      adminNotes
    });

    console.log('Force state change API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in forceStateChange API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Retry a callback for a withdrawal transaction
 *
 * @param options Options for retrying a callback
 * @returns Result of the callback retry
 */
export const retryCallback = async (options: {
  transactionId: string;
  adminNotes?: string;
}) => {
  try {
    const { transactionId, adminNotes = 'Callback retry initiated by admin' } = options;

    console.log(`Retrying callback for transaction: ${transactionId}`);

    const response = await axios.post(`${API_BASE}/wallet/admin/retry-callback`, {
      transactionId,
      adminNotes
    });

    console.log('Retry callback API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in retryCallback API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Get detailed information about a transaction, including processing history
 *
 * @param transactionId Transaction ID
 * @returns Transaction details
 */
export const getTransactionDetails = async (transactionId: string) => {
  try {
    console.log(`Fetching transaction details for: ${transactionId}`);

    const response = await axios.get(`${API_BASE}/wallet/admin/transaction-details/${transactionId}`);

    console.log('Transaction details API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in getTransactionDetails API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Fetch withdrawal monitoring data with detailed stage information
 *
 * @param filters Filter criteria
 * @param limit Maximum number of withdrawals to return
 * @param skip Number of withdrawals to skip (for pagination)
 * @param sort Sort configuration
 * @returns Withdrawal monitoring data
 */
export const fetchWithdrawalMonitor = async (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  sort?: { field: string; order: 'asc' | 'desc' }
) => {
  try {
    console.log('Fetching withdrawal monitor data with filters:', filters);

    const response = await axios.post(`${API_BASE}/withdrawal/monitor`, {
      filters,
      limit,
      skip,
      sort
    });

    console.log('Withdrawal monitor API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in fetchWithdrawalMonitor API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};